import { Box, Card, Flex, Text } from '@chakra-ui/react'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import { useEffect, useMemo, useRef, useState } from 'react'

export type MiniCalActivity = {
  id: string | number
  date: string | Date
  type?: string
  activityStartTime?: string | null
  activityEndTime?: string | null
  bankId?: number
  customerId?: number
}

export type Mini3DayCalendarProps = {
  anchorDate: Date
  isUnionSelected: boolean
  unionId: number
  selectedEntityId?: number
  getActivitiesInRangeFn: (from: Date, to: Date) => Promise<{ items?: any[] }>
  heightPx?: number
  startHour?: number
  endHour?: number
  pixelsPerHour?: number
}

function pad2(n: number) {
  return String(n).padStart(2, '0')
}

function toMinutesFromHHmm(t?: string | null) {
  if (!t) return null
  const s = String(t).trim()
  const [hh, mm] = s.split(':')
  const h = Number(hh)
  const m = Number(mm)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  return h * 60 + m
}

function labelForType(code?: string) {
  const t = String(code ?? '').toUpperCase()
  if (t === 'CONSULTING') return 'Beratung'
  if (t === 'TRAINING') return 'Schulung'
  if (t === 'TOJ_CUSTOMER') return 'Toj mit Kunde'
  if (t === 'TOJ') return 'TOJ'
  return t || 'Aktivität'
}

function dayLabel(d: dayjs.Dayjs) {
  return d.locale('de').format('dd').toUpperCase()
}

function dayNumber(d: dayjs.Dayjs) {
  return d.format('D')
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n))
}

export default function Mini3DayCalendar(props: Mini3DayCalendarProps) {
  const {
    anchorDate,
    isUnionSelected,
    unionId,
    selectedEntityId,
    getActivitiesInRangeFn,
    heightPx = 330,
    startHour = 8,
    endHour = 18,
    pixelsPerHour = 60,
  } = props

  const [items, setItems] = useState<MiniCalActivity[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const scrollRef = useRef<HTMLDivElement | null>(null)

  const days = useMemo(() => {
    const d = dayjs(anchorDate)
    return [d.subtract(1, 'day'), d, d.add(1, 'day')]
  }, [anchorDate])

  const START_MIN = startHour * 60
  const END_MIN = endHour * 60
  const TOTAL_MIN = END_MIN - START_MIN

  const totalHours = endHour - startHour
  const contentHeightPx = Math.max(totalHours * pixelsPerHour, heightPx)

  const halfHourSteps = useMemo(() => {
    const total = (endHour - startHour) * 2
    return Array.from({ length: total + 1 }, (_, i) => i)
  }, [startHour, endHour])

  const hourLabels = useMemo(() => {
    const arr: { label: string; minute: number }[] = []
    for (let h = startHour; h <= endHour; h++) {
      arr.push({ label: `${pad2(h)}:00`, minute: h * 60 })
    }
    return arr
  }, [startHour, endHour])

  useEffect(() => {
    let cancelled = false

    async function load() {
      setError(null)

      if (isUnionSelected && !selectedEntityId) {
        setItems([])
        return
      }

      const from = days[0].startOf('day').toDate()
      const to = days[2].endOf('day').toDate()

      try {
        setLoading(true)
        const res = await getActivitiesInRangeFn(from, to)
        const raw = (res.items ?? []) as MiniCalActivity[]

        const filtered = isUnionSelected
          ? raw.filter(
              a =>
                Number(a.bankId) === Number(unionId) &&
                Number(a.customerId) === Number(selectedEntityId)
            )
          : raw

        if (!cancelled) setItems(filtered)
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Kalender konnte nicht geladen werden')
        if (!cancelled) setItems([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [days, isUnionSelected, selectedEntityId, unionId, getActivitiesInRangeFn])

  const itemsByDay = useMemo(() => {
    const map = new Map<string, MiniCalActivity[]>()
    for (const d of days) map.set(d.format('YYYY-MM-DD'), [])
    for (const a of items) {
      const key = dayjs(a.date).format('YYYY-MM-DD')
      if (!map.has(key)) continue
      map.get(key)!.push(a)
    }
    for (const k of map.keys()) {
      map.get(k)!.sort((a, b) => {
        const am = toMinutesFromHHmm(a.activityStartTime) ?? 99999
        const bm = toMinutesFromHHmm(b.activityStartTime) ?? 99999
        return am - bm
      })
    }
    return map
  }, [items, days])

  const timeTopPct = (minute: number) => ((minute - START_MIN) / TOTAL_MIN) * 100

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const now = dayjs()
    const anchor = dayjs(anchorDate)

    if (!anchor.isSame(now, 'day')) {
      el.scrollTop = 0
      return
    }

    const currentMinutes = now.hour() * 60 + now.minute()
    const currentClamped = clamp(currentMinutes, START_MIN, END_MIN)
    const topRatio = (currentClamped - START_MIN) / TOTAL_MIN
    const target = topRatio * contentHeightPx - heightPx / 2

    el.scrollTop = Math.max(0, target)
  }, [anchorDate, START_MIN, END_MIN, TOTAL_MIN, contentHeightPx, heightPx])

  return (
    <Card.Root>
      <Card.Body>
        {/* Header */}
        <Flex justify="space-between" color="fg.muted" mb={3} pl="56px">
          {days.map((d, idx) => {
            const isActive = idx === 1
            return (
              <Box key={d.toString()} textAlign="center" flex="1" opacity={isActive ? 1 : 0.7}>
                <Text textStyle="metadata">{dayLabel(d)}</Text>
                <Text textStyle="highlight" color={isActive ? 'fg' : 'fg.muted'}>
                  {dayNumber(d)}
                </Text>
              </Box>
            )
          })}
        </Flex>

        {/* Scroll viewport */}
        <Box borderWidth="1px" borderColor="border" overflow="hidden">
          <Box
            ref={scrollRef}
            position="relative"
            h={`${heightPx}px`}
            overflowY="auto"
            overflowX="hidden"
            bg="bg.subtle"
            css={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <Box position="relative" h={`${contentHeightPx}px`}>
              <Flex h="full">
                {/* Time gutter */}
                <Box
                  w="46px"
                  minW="40px"
                  position="relative"
                  borderRightWidth="1px"
                  borderColor="border"
                  bg="bg"
                >
                  {hourLabels.map(h => {
                    const top = timeTopPct(h.minute)
                    return (
                      <Text
                        key={h.label}
                        position="absolute"
                        top={`${top}%`}
                        transform="translateY(-50%)"
                        textStyle="micro"
                        color="fg.muted"
                        right="10px"
                        whiteSpace="nowrap"
                      >
                        {h.label}
                      </Text>
                    )
                  })}
                </Box>

                {/* Day columns */}
                {days.map((d, idx) => {
                  const isActiveDay = idx === 1
                  const dayKey = d.format('YYYY-MM-DD')
                  const dayItems = itemsByDay.get(dayKey) ?? []

                  return (
                    <Box
                      key={d.toString()}
                      flex="1"
                      position="relative"
                      borderRightWidth={idx === 2 ? '0px' : '1px'}
                      borderColor="border"
                      bg={isActiveDay ? 'bg' : 'bg.subtle'}
                    >
                      {/* Grid lines */}
                      {halfHourSteps.slice(1).map(step => {
                        const minute = START_MIN + step * 30
                        const top = timeTopPct(minute)
                        const isFullHour = step % 2 === 0

                        return (
                          <Box
                            key={step}
                            position="absolute"
                            left={0}
                            right={0}
                            top={`${top}%`}
                            borderTopWidth="1px"
                            borderColor="border"
                            opacity={isFullHour ? 0.9 : 0.5}
                          />
                        )
                      })}

                      {/* Events */}
                      {dayItems.map(a => {
                        const start = toMinutesFromHHmm(a.activityStartTime)
                        const end = toMinutesFromHHmm(a.activityEndTime)

                        if (start == null || end == null || end <= start) {
                          return (
                            <Box
                              key={String(a.id)}
                              mx={2}
                              mt={2}
                              bg="bg"
                              borderWidth="1px"
                              borderColor="border"
                              color="fg.muted"
                            >
                              <Text textStyle="body">{labelForType(a.type)}</Text>
                            </Box>
                          )
                        }

                        const startClamped = clamp(start, START_MIN, END_MIN)
                        const endClamped = clamp(end, START_MIN, END_MIN)
                        const topPct = ((startClamped - START_MIN) / TOTAL_MIN) * 100
                        const heightPct = ((endClamped - startClamped) / TOTAL_MIN) * 100

                        const bg = isActiveDay ? 'blue.emphasized' : 'bg.info'
                        const borderColor = isActiveDay ? 'blue.emphasized' : 'bg.info'
                        const textColor = isActiveDay ? 'fg.info' : 'fg.subtle'

                        return (
                          <Box
                            key={String(a.id)}
                            position="absolute"
                            top={`${topPct}%`}
                            height={`${heightPct}%`}
                            left="0"
                            right="0"
                            bg={bg}
                            borderWidth="1px"
                            borderColor={borderColor}
                            px={2}
                            py={1.5}
                            overflow="hidden"
                          >
                            <Text textStyle="2xsMedium" color={textColor}>
                              {labelForType(a.type)}
                            </Text>
                          </Box>
                        )
                      })}
                    </Box>
                  )
                })}
              </Flex>

              {loading && (
                <Box
                  position="absolute"
                  inset={0}
                  bg="bg/60"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text textStyle="body" color="fg.muted">
                    Lade Aktivitäten…
                  </Text>
                </Box>
              )}

              {error && !loading && (
                <Box
                  position="absolute"
                  inset={0}
                  bg="bg/60"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  px={4}
                >
                  <Text textStyle="body" color="fg.error" textAlign="center">
                    {error}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Card.Body>
    </Card.Root>
  )
}
